import { useQueryClient } from 'react-query'
import { sendToApi } from 'api/client'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { useFlipperFlag } from 'hooks/useFlipperFlag'

const MINUTE = 1000 * 60

type HighResVideoResponse = {
  url: string
}

const fetchHighResVideo = async (video_id: string) => {
  const response = await sendToApi<HighResVideoResponse>(`/storyblocks/videos/${video_id}/download`)

  if (response.type !== SUCCESS_RESPONSE_TYPE) {
    throw new Error(response.data)
  }

  return response.data.url
}

/**
 * Hook specifically for downloading high-resolution stock videos.
 * Only works with Storyblocks provider when the 'stock_videos' flag is enabled.
 * Returns null if the feature flag is disabled.
 *
 */
export const useStockVideoDownload = () => {
  const useStoryblocksProvider = useFlipperFlag('stock_videos')
  const queryClient = useQueryClient()

  const downloadHighRes = async (videoId: string): Promise<string | null> => {
    if (!useStoryblocksProvider) {
      return null
    }

    const url = await queryClient.fetchQuery(['storyblocks-download', videoId], () => fetchHighResVideo(videoId), {
      cacheTime: MINUTE * 10,
      staleTime: MINUTE * 5,
      retry: 1,
    })

    return url
  }

  return {
    downloadHighRes,
  }
}
